/**
 * Auth wrapper that hides the choice between real Clerk and a dev bypass.
 *
 * `VITE_AUTH_DEV_BYPASS=1` (set by the e2e job and `npm run dev` defaults)
 * skips Clerk entirely and emits a static `dev:e2e-test` bearer token. The
 * cloud API runs in the same bypass mode, so the token round-trips through
 * verification without touching a real Clerk tenant.
 *
 * Components consume a single `useAuth()` regardless of mode; the gating
 * happens at the provider level so we never call hooks conditionally.
 */

import {
  ClerkProvider,
  SignIn,
  useAuth as useClerkAuth,
  useClerk,
} from "@clerk/clerk-react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import { Button, buttonClasses, type ButtonProps } from "@fprint/ui";

import {
  clearResumeQuoteId,
  clerkRedirectProps,
  markQuoteResumeFromSignInTarget,
  markResumeAfterAuth,
  orderResumePath,
  persistResumeQuoteId,
  quoteIdFromOrderPath,
  resolveSignInRedirect,
  takeResumeQuoteIdAfterAuth,
} from "./resumeQuote";

const DEV_BYPASS = import.meta.env.VITE_AUTH_DEV_BYPASS === "1";
const CLERK_PK = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? "";
const DEV_SUBJECT = import.meta.env.VITE_DEV_AUTH_SUBJECT ?? "dev:e2e-test";

interface AuthContextValue {
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function DevAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      isLoaded: true,
      isSignedIn: true,
      getToken: async () => DEV_SUBJECT,
      signOut: async () => {
        // Dev bypass is always "signed in" for testing convenience.
        // A no-op is intentional; real sign-out only exists in Clerk mode.
      },
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function ClerkAdapter({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const clerk = useClerk();
  const value = useMemo<AuthContextValue>(
    () => ({
      isLoaded: Boolean(isLoaded),
      isSignedIn: Boolean(isSignedIn),
      getToken: () => getToken(),
      signOut: () => clerk.signOut(),
    }),
    [isLoaded, isSignedIn, getToken, clerk],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (DEV_BYPASS) return <DevAuthProvider>{children}</DevAuthProvider>;
  if (!CLERK_PK) {
    return (
      <div className="p-8 text-danger" role="alert">
        VITE_CLERK_PUBLISHABLE_KEY is not set and VITE_AUTH_DEV_BYPASS is not enabled.
      </div>
    );
  }
  return (
    <ClerkProvider publishableKey={CLERK_PK}>
      <ClerkAdapter>{children}</ClerkAdapter>
    </ClerkProvider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    // Clerk is still determining the session (common on initial mount,
    // after redirects, or hard navigations). Render a minimal placeholder
    // instead of immediately redirecting or showing blank content.
    return (
      <main className="mx-auto flex min-h-screen items-center justify-center px-6">
        <p className="text-muted-fg">Loading…</p>
      </main>
    );
  }

  if (!isSignedIn) {
    const from = `${location.pathname}${location.search}`;
    const quoteId = quoteIdFromOrderPath(location.pathname);
    if (quoteId) {
      persistResumeQuoteId(quoteId);
      markResumeAfterAuth();
    }
    return (
      <Navigate
        to={`/sign-in?from=${encodeURIComponent(from)}`}
        state={{ from }}
        replace
      />
    );
  }

  return <>{children}</>;
}

export function SignInPage() {
  // Hooks must run unconditionally (Rules of Hooks) — call useLocation
  // / useEffect before any early return, even though DEV_BYPASS is a
  // build constant.
  const location = useLocation();
  const stateFrom = (location.state as { from?: string } | null)?.from;
  const queryFrom = new URLSearchParams(location.search).get("from");
  const redirectUrl = resolveSignInRedirect(stateFrom || queryFrom);

  useEffect(() => {
    if (DEV_BYPASS) return;
    if (!redirectUrl.startsWith("/order/")) return;
    const quoteId = quoteIdFromOrderPath(redirectUrl);
    if (!quoteId) return;
    persistResumeQuoteId(quoteId);
    markResumeAfterAuth();
  }, [redirectUrl]);

  if (DEV_BYPASS) {
    // Should never reach here — RequireAuth lets bypass users through.
    return <Navigate to="/" replace />;
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md items-center justify-center px-6 py-12">
      <SignIn
        routing="path"
        path="/sign-in"
        {...clerkRedirectProps(redirectUrl)}
      />
    </main>
  );
}

/**
 * Safety net when Clerk dumps the customer on `/` or `/quote` after
 * **register** (dashboard after-sign-up URL). Existing-account sign-in
 * already lands on `/order/:id` via fallbackRedirectUrl — if we are
 * already there this is a no-op. One-shot: only fires when the tab
 * marked a resume before the OTP round-trip.
 */
export function AuthResumeRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const id = takeResumeQuoteIdAfterAuth();
    if (!id) return;
    if (location.pathname === `/order/${id}`) return;
    window.location.assign(orderResumePath(id));
  }, [isLoaded, isSignedIn, location.pathname]);

  return null;
}

/**
 * Sign out button.
 * - Hidden in dev bypass mode (always "signed in" for testing).
 * - Uses Clerk's signOut in real mode (clears session + redirects).
 */
export function SignOutButton({
  size = "md",
  className,
}: Pick<ButtonProps, "size" | "className"> = {}) {
  const { isLoaded, isSignedIn, signOut } = useAuth();

  if (!isLoaded || !isSignedIn || DEV_BYPASS) {
    return null;
  }

  const handleSignOut = async () => {
    // Leftover resume ids must not survive sign-out — the next Home
    // sign-in would otherwise hard-nav to a stale /order/:id.
    clearResumeQuoteId();
    await signOut();
    // After Clerk clears the session we force a navigation to the public root.
    // A hard location change guarantees no stale auth state in the SPA.
    window.location.assign("/");
  };

  return (
    <Button variant="secondary" size={size} className={className} onClick={handleSignOut}>
      Sign out
    </Button>
  );
}

/**
 * Sign in button for logged-out users.
 * - Hidden in dev bypass mode (always "signed in" for testing).
 * - Hidden when already signed in.
 * - Links to the Clerk sign-in flow (path routing), carrying the current
 *   location as `from` state so SignInPage can return the user to where
 *   they were (instead of dropping them on "/").
 * - `from` can be overridden by callers that know a better resume target
 *   (e.g. the quote page passes its /order/:quoteId deep link).
 */
export function SignInButton({
  from,
  size = "md",
  className,
}: { from?: string } & Pick<ButtonProps, "size" | "className"> = {}) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded || isSignedIn || DEV_BYPASS) {
    return null;
  }

  const target = from ?? `${location.pathname}${location.search}`;

  return (
    <Link
      to={`/sign-in?from=${encodeURIComponent(target)}`}
      state={{ from: target }}
      className={buttonClasses({ variant: "secondary", size, className })}
      onClick={() => {
        markQuoteResumeFromSignInTarget(target);
      }}
    >
      Sign in
    </Link>
  );
}
